export interface FilterOption {
  label: string;
  value: string;
}

/**
 * Page through a lookup endpoint until every option has been fetched, so
 * filter dropdowns are never silently capped at a fixed page size.
 *
 * `fetchPage` returns one page of options plus the total available. When the
 * total is known we stop once we have it all; when it is null we stop as soon
 * as a page returns fewer rows than requested (the last page). The iteration
 * ceiling is only an infinite-loop backstop, not a result cap.
 */
export async function loadAllFilterOptions(
  fetchPage: (
    page: number,
    pageSize: number,
  ) => Promise<{ options: FilterOption[]; totalCount: number | null }>,
  pageSize = 500,
): Promise<FilterOption[]> {
  const all: FilterOption[] = [];
  let page = 1;

  for (let i = 0; i < 1000; i += 1) {
    const { options, totalCount } = await fetchPage(page, pageSize);
    all.push(...options);

    const reachedTotal = totalCount !== null && all.length >= totalCount;
    if (options.length < pageSize || reachedTotal) {
      break;
    }
    page += 1;
  }

  return all;
}
